import { useState } from "react";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt    = n => "$" + Math.round(n ?? 0).toLocaleString("es-CL");
const f1     = n => (n ?? 0).toFixed(1);
const f2     = n => (n ?? 0).toFixed(2);
const mono   = { fontFamily: "'JetBrains Mono', 'Courier New', monospace" };

// ── Íconos de mano ────────────────────────────────────────────────────────────
function ThumbUp({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/>
      <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/>
    </svg>
  );
}

function ThumbDown({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z"/>
      <path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/>
    </svg>
  );
}

// ── Cálculo de un escenario ───────────────────────────────────────────────────
export function calcEscenario(p, TC_s, TE_s, meta_s) {
  const ent_r  = p.entregados     || 0;
  const fall_r = p.fallidos       || 0;
  const total  = p.total_pedidos  || 0;

  const price_unit    = ent_r  > 0 ? p.venta_total       / ent_r  : 0;
  const costo_unit    = ent_r  > 0 ? p.costo_proveedor   / ent_r  : 0;
  const flete_e_unit  = ent_r  > 0 ? p.flete_entregados  / ent_r  : 0;
  const flete_f_unit  = fall_r > 0 ? p.flete_fallidos    / fall_r : 0;

  const confirmados_s = total * (TC_s / 100);
  const entregados_s  = confirmados_s * (TE_s / 100);
  const fallidos_s    = confirmados_s * (1 - TE_s / 100);

  const venta_s    = entregados_s * price_unit;
  const margen_s   = venta_s
    - entregados_s * costo_unit
    - entregados_s * flete_e_unit
    - fallidos_s   * flete_f_unit;
  const ganancia   = margen_s - meta_s;

  const roas       = meta_s > 0 ? venta_s / meta_s : 0;
  const margen_pct = venta_s > 0 ? (ganancia / venta_s) * 100 : 0;
  const cpa        = total   > 0 ? meta_s  / total : 0;

  return { ganancia, roas, margen_pct, cpa, entregados: entregados_s, venta: venta_s };
}

// ── Recomendación unificada ───────────────────────────────────────────────────
function calcRecomendacion(ganBase, ganAcid, ganOpt) {
  if (ganAcid < 0 && ganBase < 0)
    return {
      tipo: "apagar", icono: "down", color: "#DC2626", bg: "#FEF2F2",
      titulo: "Apagar campaña",
      texto: "Este producto pierde dinero hoy y en el peor escenario. La acción recomendada es pausar la campaña de Meta Ads mientras revisás el precio de venta, los costos o la logística.",
    };
  if (ganBase < 0)
    return {
      tipo: "revisar", icono: "down", color: "#EF9F27", bg: "#FFFBEB",
      titulo: "Pausar y revisar",
      texto: "El producto ya está generando pérdida en el período real. Seguir invirtiendo en pauta sin corregir precio o costos amplifica la pérdida. Pausá hasta resolver el problema de base.",
    };
  if (ganAcid < 0)
    return {
      tipo: "cuidado", icono: "up", color: "#EF9F27", bg: "#FFFBEB",
      titulo: "Escalar con cautela",
      texto: "El producto es rentable hoy, pero el escenario ácido lo pone en rojo. Escalá solo si primero lográs mejorar la tasa de entrega — ahí sí tiene recorrido real.",
    };
  return {
    tipo: "escalar", icono: "up", color: "#059669", bg: "#ECFDF5",
    titulo: "Producto para escalar",
    texto: "Rentable en los 3 escenarios: base, ácido y optimista. Aumentar el presupuesto de Meta Ads tiene alta probabilidad de generar más margen. Este es tu candidato prioritario de inversión.",
  };
}

// ── Guía didáctica (expandible) ───────────────────────────────────────────────
function GuiaEscenarios({ deltaAcid, deltaOpt, factorOpt }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <button
        onClick={() => setAbierto(v => !v)}
        style={{
          width: "100%", textAlign: "left", background: "none", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
          padding: "6px 0", fontFamily: "inherit",
        }}
      >
        <span style={{ fontSize: 14 }}>💡</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#4338CA" }}>
          ¿Cómo leer esta simulación?
        </span>
        <span style={{ fontSize: 10, color: "#9496B4", marginLeft: "auto" }}>
          {abierto ? "▲ cerrar" : "▼ ver explicación"}
        </span>
      </button>

      {abierto && (
        <div style={{
          background: "#EEF2FF", border: "1px solid rgba(67,56,202,0.15)",
          borderRadius: 10, padding: "14px 16px", fontSize: 12, color: "#374151",
          lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 10,
        }}>
          <p style={{ margin: 0 }}>
            <strong>¿Qué estamos simulando?</strong> Proyectamos cómo le iría al producto
            si la operación cambiara. Usamos tus números reales como punto de partida y
            calculamos 3 futuros posibles.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#fff", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontWeight: 600, color: "#4338CA", marginBottom: 4 }}>TC — Tasa de Confirmación</div>
              <div>
                De cada 100 pedidos que entran, ¿cuántos el cliente confirma que quiere recibir?
                Si TC = 60%, 60 siguen adelante y 40 se cancelan antes de salir.
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontWeight: 600, color: "#4338CA", marginBottom: 4 }}>TE — Tasa de Entrega</div>
              <div>
                De los pedidos que salieron a ruta, ¿cuántos llegaron al cliente?
                Si TE = 70%, 70 se entregaron y 30 volvieron como devoluciones (y pagaste el flete igual).
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontWeight: 600, color: "#4338CA", marginBottom: 4 }}>ROAS</div>
              <div>
                Por cada peso invertido en Meta Ads, ¿cuánto retornaste en venta?
                ROAS 1.5x = por cada $100 de pauta, generaste $150 en ventas. El mínimo sano es 1.3x–1.5x.
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontWeight: 600, color: "#4338CA", marginBottom: 4 }}>CPA</div>
              <div>
                Costo por Adquisición: cuánto pagaste en publicidad por cada pedido generado.
                Si Meta gastó $50.000 y tuviste 20 pedidos, el CPA es $2.500.
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(67,56,202,0.15)", paddingTop: 10 }}>
            <strong>Los 3 escenarios de este producto:</strong>
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 5 }}>
              <div>
                📊 <strong>Caso Base</strong> — Lo que está pasando HOY. Usamos los números reales del período.
              </div>
              <div>
                ⚠️ <strong>Caso Ácido</strong> — Si las cosas van peor. TC y TE bajan {deltaAcid} puntos porcentuales.
                "Si la logística empeora y más pedidos rebotan, ¿seguiría siendo negocio?"
              </div>
              <div>
                ↑ <strong>Caso Optimista</strong> — Si las cosas mejoran. TC y TE suben {deltaOpt} puntos y Meta se
                vuelve {Math.round((1 - factorOpt) * 100)}% más eficiente. "¿Cuánto podría ganar si optimizo?"
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Card de un escenario ──────────────────────────────────────────────────────
const ESTILOS = {
  base:      { border: "#D1D5DB", bg: "#F9FAFB", badgeBg: "#F3F4F6", badgeColor: "#374151", icon: "📊", label: "Caso Base"      },
  acido:     { border: "#DC2626", bg: "#FEF2F2", badgeBg: "#FEE2E2", badgeColor: "#991B1B", icon: "⚠️", label: "Caso Ácido"     },
  optimista: { border: "#059669", bg: "#ECFDF5", badgeBg: "#D1FAE5", badgeColor: "#065F46", icon: "↑",  label: "Caso Optimista" },
};

const DESCRIPCIONES_TIPO = {
  base:      (tc, te) => `Números reales del período: ${f1(tc)}% confirmación, ${f1(te)}% entrega.`,
  acido:     (tc, te) => `Si la logística empeora: ${f1(tc)}% confirmación y ${f1(te)}% entrega.`,
  optimista: (tc, te) => `Si la operación mejora: ${f1(tc)}% confirmación y ${f1(te)}% entrega.`,
};

function EscenarioCard({ tipo, tc, te, meta, resultado }) {
  const est = ESTILOS[tipo];
  const { ganancia, roas, margen_pct, cpa } = resultado;
  const gPos = ganancia >= 0;

  return (
    <div style={{
      border: `1.5px solid ${est.border}`,
      background: est.bg,
      borderRadius: 12,
      padding: "1rem 1.1rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.6rem",
    }}>
      {/* Badge + descripción */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            background: est.badgeBg, color: est.badgeColor,
          }}>
            {est.icon} {est.label}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.45 }}>
          {DESCRIPCIONES_TIPO[tipo]?.(tc, te)}
        </div>
      </div>

      {/* Parámetros usados */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[
          { k: "TC", v: f1(tc) + "%" },
          { k: "TE", v: f1(te) + "%" },
          { k: "Meta", v: fmt(meta) },
        ].map(({ k, v }) => (
          <div key={k} style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 6,
            background: "#fff", border: "1px solid " + est.border + "60",
            color: "#6B7280",
          }}>
            <span style={{ fontWeight: 600, color: "#374151" }}>{k}</span> {v}
          </div>
        ))}
      </div>

      <div style={{ borderTop: "1px solid " + est.border + "40", paddingTop: "0.6rem" }} />

      {/* Ganancia — grande */}
      <div>
        <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
          Ganancia proyectada
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: gPos ? "#059669" : "#DC2626", ...mono }}>
          {fmt(ganancia)}
        </div>
        <div style={{ fontSize: 11, color: gPos ? "#059669" : "#DC2626", marginTop: 2 }}>
          {gPos ? "✓ Rentable en este escenario" : "✗ Pérdida en este escenario"}
        </div>
      </div>

      {/* Métricas */}
      {[
        { label: "ROAS proyectado",     value: f2(roas) + "x",      color: roas >= 1.3 ? "#059669" : roas > 0 ? "#EF9F27" : "#9CA3AF" },
        { label: "Margen neto %",       value: f1(margen_pct) + "%", color: margen_pct >= 0 ? "#059669" : "#DC2626" },
        { label: "CPA proyectado",      value: fmt(cpa),             color: "#374151" },
      ].map(({ label, value, color }) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#6B7280" }}>{label}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color, ...mono }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Panel de parámetros colapsable ────────────────────────────────────────────
export function SimulacionParams({ params, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" }}>
      {[
        { key: "deltaAcid", label: "Delta Ácido (pp)",          hint: "Puntos porcentuales a restar en TC y TE para el caso ácido", min: 1, max: 30 },
        { key: "deltaOpt",  label: "Delta Optimista (pp)",       hint: "Puntos porcentuales a sumar en TC y TE para el caso optimista",  min: 1, max: 30 },
        { key: "factorOpt", label: "Eficiencia Meta optimista",  hint: "Factor multiplicador del gasto Meta (0.5 = 50% más eficiente)", min: 0.1, max: 1, step: 0.05 },
      ].map(({ key, label, hint, min, max, step }) => (
        <div key={key}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#4B4D6E", marginBottom: 4 }}>{label}</div>
          <input
            type="number"
            min={min}
            max={max}
            step={step || 1}
            value={params[key]}
            onChange={e => onChange({ ...params, [key]: parseFloat(e.target.value) || min })}
            style={{
              width: "100%", padding: "7px 10px", border: "1px solid #E4E6F5",
              borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box",
              ...mono,
            }}
          />
          <div style={{ fontSize: 10, color: "#9496B4", marginTop: 2 }}>{hint}</div>
        </div>
      ))}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function SimulacionEscenarios({ p, params }) {
  const confirmados_r = (p.total_pedidos || 0) - (p.pendientes || 0) - (p.cancelados || 0);
  const TC_real = p.total_pedidos > 0 ? (confirmados_r / p.total_pedidos) * 100 : 0;
  const TE_real = p.tasa_entrega || 0;

  const { deltaAcid, deltaOpt, factorOpt } = params;

  const TC_acid = Math.max(TC_real - deltaAcid, 5);
  const TE_acid = Math.max(TE_real - deltaAcid, 5);
  const TC_opt  = Math.min(TC_real + deltaOpt,  95);
  const TE_opt  = Math.min(TE_real + deltaOpt,  95);

  const meta     = p.gasto_meta || 0;
  const meta_opt = meta * factorOpt;

  const resBase = calcEscenario(p, TC_real, TE_real, meta);
  const resAcid = calcEscenario(p, TC_acid, TE_acid, meta);
  const resOpt  = calcEscenario(p, TC_opt,  TE_opt,  meta_opt);

  const rec = calcRecomendacion(resBase.ganancia, resAcid.ganancia, resOpt.ganancia);

  const escenarios = [
    { tipo: "base",      tc: TC_real, te: TE_real, meta,          resultado: resBase },
    { tipo: "acido",     tc: TC_acid, te: TE_acid, meta,          resultado: resAcid },
    { tipo: "optimista", tc: TC_opt,  te: TE_opt,  meta: meta_opt, resultado: resOpt },
  ];

  return (
    <div style={{ background: "#F7F8FE", border: "1px solid #E4E6F5", borderRadius: 12, padding: "1rem 1.1rem", marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#9496B4", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.85rem" }}>
        Simulación de Escenarios
      </div>

      {/* ── Recomendación unificada (arriba) ── */}
      <div style={{
        background: rec.bg,
        border: `1.5px solid ${rec.color}40`,
        borderRadius: 12,
        padding: "0.9rem 1.1rem",
        marginBottom: "0.9rem",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}>
        <div style={{
          flexShrink: 0,
          width: 48, height: 48,
          borderRadius: 12,
          border: `1.5px solid ${rec.color}50`,
          background: `${rec.color}0D`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {rec.icono === "up" ? <ThumbUp size={26} /> : <ThumbDown size={26} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: rec.color, marginBottom: 4 }}>
            {rec.titulo}
          </div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.55 }}>{rec.texto}</div>
        </div>
      </div>

      {/* ── Guía didáctica ── */}
      <GuiaEscenarios deltaAcid={deltaAcid} deltaOpt={deltaOpt} factorOpt={factorOpt} />

      {/* ── 3 columnas de escenarios ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" }}>
        {escenarios.map(e => (
          <EscenarioCard key={e.tipo} {...e} />
        ))}
      </div>
    </div>
  );
}
